import { callFirebaseFunction } from 'boot/firebase';
import { documentId, Timestamp } from 'firebase/firestore';
import { chain, chunk, find, get, includes, isDate, isFunction, map, range } from 'lodash';
import { mapActions, mapState } from 'pinia';
import Time from 'src/mixins/Time';
import { HUMANIZED_INTEGRATION_NAMES } from 'src/constants/integrations';
import { ACTIVITY_STATUSES_FOR_APPOINTMENTS } from 'src/constants/sessionActivities';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { network } from 'src/store/network';
import { units } from 'src/store/units';
import { logEvent } from 'src/utils/LogEvent';
import AppUtils from 'src/utils/AppUtils';

export default {

  mixins: [Toolbox, Time],

  data() {
    return {
      appointmentStatusColors: {
        created: 'gray',
        draft: 'gray',
        scheduled: null,
        'in-progress': 'orange',
        cancelled: 'red',
        completed: 'green',

        // SessionActivity-derived statuses for appointments with linked activities
        // 'in-progress' already exists on the appointment; 'closed' maps to 'completed'
        [ACTIVITY_STATUSES_FOR_APPOINTMENTS.PENDING_NOTES]: 'orange',
        [ACTIVITY_STATUSES_FOR_APPOINTMENTS.PENDING_REVIEW]: 'orange',
        [ACTIVITY_STATUSES_FOR_APPOINTMENTS.PENDING_SIGNATURES]: 'orange',
        [ACTIVITY_STATUSES_FOR_APPOINTMENTS.RENDERED]: 'green',
      },

      appointmentChangeFormatting: {
        type: {
          label: this.$t('scheduling.labelType'),
          format: (type) => (this.getAppointmentTypeLabel({ type })),
          order: 5,
        },
        status: {
          label: this.$t('scheduling.labelStatus'),
          format: (status) => (this.getAppointmentStatusLabel({ status })),
          order: 10,
        },
        client: {
          label: this.$t('scheduling.labelClient'),
          format: (clientId) => (chain(this.clients).find({ id: clientId }).get('clientName', null).value()),
          order: 15,
        },
        cancellationReason: {
          label: this.$t('scheduling.labelCancellationReason'),
          format: (cancellationReason) => (chain(this.cancellationReasons)
            .find({ id: cancellationReason })
            .get('cancellationReason')
            .value()),
          order: 20,
        },
        cancellationType: {
          label: this.$t('scheduling.labelCancellationType'),
          format: (cancellationType) => (
            cancellationType
              ? get(this.$tm('cancellationTypes'), cancellationType, null)
              : null
          ),
          order: 30,
        },
        scheduledStartTimestamp: {
          label: this.$t('scheduling.labelStartTime'),
          format: (scheduledStartTimestamp, obj, otherObj) => {
            if (!scheduledStartTimestamp) {
              return this.$t('common.placeholderEmpty');
            }
            if (!this.isAppointmentDateChanged(scheduledStartTimestamp, otherObj?.scheduledStartTimestamp)) {
              return this.getLocaleTimeString(scheduledStartTimestamp, {
                options: { hour: 'numeric', minute: '2-digit' },
              });
            }
            return scheduledStartTimestamp instanceof Timestamp ? this.getLocaleString(scheduledStartTimestamp) : '';
          },
          order: 40,
        },
        duration: {
          label: this.$t('scheduling.labelDuration'),
          format: (duration) => (this.formatDuration(duration)),
          order: 50,
        },
        recurrenceLabel: {
          label: this.$t('scheduling.commitChanges.labelRepeat'),
          order: 60,
        },
        sessionName: {
          label: this.$t('scheduling.labelSession'),
          order: 70,
        },
        userName: {
          label: this.$t('scheduling.labelUser'),
          order: 80,
        },
        placeOfService: {
          label: this.$t('scheduling.labelPlaceOfServiceType'),
          format: (placeOfService) => (chain(this.placesOfService)
            .find({ id: placeOfService })
            .get('placeOfServiceName')
            .value()),
          order: 90,
        },
        // Integration clinics store PoS display name directly instead of a Hirasmus ID
        placeOfServiceName: {
          label: this.$t('scheduling.labelPlaceOfServiceType'),
          order: 91,
        },
        placeOfServiceAddress: {
          label: this.$t('scheduling.labelPlaceOfServiceAddress'),
          order: 100,
        },
        treatmentService: {
          label: this.$t('scheduling.labelTreatmentService'),
          format: (treatmentServiceId) => (chain(this.treatmentServices)
            .find({ id: treatmentServiceId })
            .get('serviceName')
            .value()),
          order: 110,
        },
        // Integration clinics store service display label directly instead of a Hirasmus ID
        integrationServiceName: {
          label: this.$t('scheduling.labelTreatmentService'),
          order: 111,
        },
        notes: {
          label: this.$t('scheduling.labelAppointmentNotes'),
          order: 120,
        },
        isNotesVisible: {
          label: this.$t('scheduling.labelIsNotesVisible'),
          format: (v) => {
            if (typeof v !== 'boolean') {
              return this.$t('common.placeholderEmpty');
            }
            return (v ? this.$t('common.labelVisible') : this.$t('common.labelHidden'));
          },
          order: 130,
        },
        isPayable: {
          label: this.$t('scheduling.labelIsPayable'),
          format: (v) => {
            if (typeof v !== 'boolean') {
              return this.$t('common.placeholderEmpty');
            }
            return (v ? this.$t('common.buttonYes') : this.$t('common.buttonNo'));
          },
          order: 140,
        },
        isBillable: {
          label: this.$t('scheduling.labelIsNonBillable'),
          format: (v) => {
            if (typeof v !== 'boolean') {
              return this.$t('common.placeholderEmpty');
            }
            return (v ? this.$t('common.buttonNo') : this.$t('common.buttonYes'));
          },
          order: 150,
        },
      },
    };
  },

  async created() {
    // Lazily load treatment services the first time any appointment surface (which all
    // mix this in) mounts, instead of eagerly at login.
    await AppUtils.allSettled([
      this.ensureTreatmentServicesBound(),
      this.ensurePlacesOfServiceBound(),
    ]);
  },

  computed: {
    ...mapState(auth, ['appointmentThreshold', 'connectedClinic', 'integrationSettings', 'placesOfService',
      'treatmentServices', 'placesOfService', 'cancellationReasons', 'hasPremiumScheduling']),
    ...mapState(network, ['offlineMode']),
    ...mapState(units, ['units']),
    ...mapState(clients, ['clients']),

    appointmentStatuses() {
      return {
        created: {
          label: this.$t('appointments.status.created'),
          icon: 'fas fa-file',
          color: 'grey',
          bgClass: 'bg-grey-6',
          textClass: 'text-white',
        },
        draft: {
          label: this.$t('appointments.status.draft'),
          icon: 'fas fa-file',
          color: 'grey',
          bgClass: 'bg-grey-6',
          textClass: 'text-white',
        },
        scheduled: {
          label: this.$t('appointments.status.scheduled'),
          icon: 'calendar_today',
          color: 'primary',
          bgClass: 'bg-primary',
          textClass: 'text-white',
        },
        'in-progress': {
          label: this.$t('appointments.status.in-progress'),
          icon: 'play_arrow',
          color: 'orange',
          bgClass: 'bg-orange-6',
          textClass: 'text-white',
        },

        // SessionActivity-derived statuses for appointments with linked activities
        // 'in-progress' already exists on the appointment; 'closed' maps to 'completed'
        [ACTIVITY_STATUSES_FOR_APPOINTMENTS.PENDING_NOTES]: {
          label: this.$t('sessions.modals.sessionActivity.statuses.finished'),
          icon: 'pending_actions',
          color: 'warning',
          bgClass: 'bg-orange-6',
          textClass: 'text-white',
        },
        [ACTIVITY_STATUSES_FOR_APPOINTMENTS.PENDING_REVIEW]: {
          label: this.$t('sessions.modals.sessionActivity.statuses.pending-review'),
          icon: 'rate_review',
          color: 'warning',
          bgClass: 'bg-orange-6',
          textClass: 'text-white',
        },
        [ACTIVITY_STATUSES_FOR_APPOINTMENTS.PENDING_SIGNATURES]: {
          label: this.$t('sessions.modals.sessionActivity.statuses.pending-signatures'),
          icon: 'fas fa-file-signature',
          color: 'warning',
          bgClass: 'bg-orange-6',
          textClass: 'text-white',
        },
        [ACTIVITY_STATUSES_FOR_APPOINTMENTS.RENDERED]: {
          label: this.$t('sessions.modals.sessionActivity.statuses.rendered', {
            integration:
              HUMANIZED_INTEGRATION_NAMES[this.connectedClinic?.integration] ?? this.connectedClinic?.integration,
          }),
          icon: 'published_with_changes',
          color: 'green',
          bgClass: 'bg-green-6',
          textClass: 'text-white',
        },

        completed: {
          label: this.$t('appointments.status.completed'),
          icon: 'check_circle',
          color: 'green',
          bgClass: 'bg-green-6',
          textClass: 'text-white',
        },
        cancelled: {
          label: this.$t('appointments.status.cancelled'),
          icon: 'event_busy',
          color: 'red',
          bgClass: 'bg-red-6',
          textClass: 'text-white',
        },
        deleted: {
          label: this.$t('appointments.status.deleted'),
          icon: 'event_busy',
          color: 'red',
          bgClass: 'bg-red-6',
          textClass: 'text-white',
        },
        unknown: {
          label: this.$t('appointments.status.unknown'),
          icon: 'warning',
          color: 'grey',
          hidden: true,
          bgClass: 'bg-primary',
          textClass: 'text-white',
        },

      };
    },
  },

  methods: {
    ...mapActions(auth, ['ensureTreatmentServicesBound', 'ensurePlacesOfServiceBound']),

    getAppointmentPlaceOfServiceAddress(placeOfServiceType, defaultAddress, client) {
      switch (placeOfServiceType) {
        case 'clientUnit': {
          const unit = find(
            this.units,
            { id: chain(client).get('units').head().value() },
          );
          if (unit) {
            return this.formatPlaceOfServiceAddress(
              unit.address,
              unit.city,
              unit.state,
              unit.postalCode,
            ) || null;
          }
          return null;
        }
        case 'clientHome': {
          const clientAddress = chain(client)
            .pick(['address1', 'address2'])
            .values()
            .join(' ')
            .trim()
            .value();
          return this.formatPlaceOfServiceAddress(
            clientAddress,
            get(client, 'city'),
            get(client, 'state'),
            get(client, 'postalCode'),
          ) || null;
        }
        case 'other':
        default: {
          return defaultAddress || null;
        }
      }
    }, // getAppointmentPlaceOfServiceAddress()

    formatPlaceOfServiceLabel(appointment, client) {
      let { placeOfServiceName } = appointment;
      const { placeOfServiceAddress } = appointment;
      let placeOfServiceLocation;

      if (!appointment.placeOfService) {
        placeOfServiceLocation = placeOfServiceAddress;
      } else {
        const placeOfService = find(this.placesOfService, { id: appointment.placeOfService });
        placeOfServiceName = placeOfService?.placeOfServiceName || null;

        switch (placeOfService?.addressSource) {
          case 'other':
            // Location label is the address listed on the appointment
            placeOfServiceLocation = placeOfServiceAddress;
            break;
          case 'clientUnit': {
            // Location label is just the business unit name
            placeOfServiceLocation = chain(this.units)
              .find({
                id: chain(client)
                  .get('units')
                  .head()
                  .value(),
              })
              .get('unitName')
              .value() || null;
            break;
          }
          default: {
            // Location label is the address extracted based on the Place of Service location type
            placeOfServiceLocation = this.getAppointmentPlaceOfServiceAddress(
              placeOfService?.addressSource,
              this.formatPlaceOfServiceAddress(
                placeOfService.customAddress?.address1,
                placeOfService.customAddress?.city,
                placeOfService.customAddress?.state,
                placeOfService.customAddress?.postalCode,
              ) || '',
              client,
            );
          }
        }
      }

      if (placeOfServiceName) {
        if (placeOfServiceLocation) {
          return `${placeOfServiceLocation} (${placeOfServiceName})`;
        }
        return placeOfServiceName;
      }
      return placeOfServiceLocation || '';
    }, // formatPlaceOfServiceLabel()

    formatPlaceOfServiceAddress(address, city, state, postalCode) {
      if (!address) {
        return null;
      }
      return `${address}, ${city || ''}, ${state || ''} ${postalCode || ''}`;
    },

    /**
     * Check if appointment is available scheduled within the threshold that makes it
     * available to run.
     * @param {*} scheduledStartTimestamp
     * @param {*} scheduledEndTimestamp
     * @returns True if the appointment is available to run, false otherwise.
     */
    isAppointmentAvailableToRun(scheduledStartTimestamp, scheduledEndTimestamp) {
      if (!isDate(scheduledStartTimestamp) || !isDate(scheduledEndTimestamp)) {
        return false;
      }
      const currentDate = new Date();
      const timeFromAppointmentStart = (scheduledStartTimestamp.getTime() - Date.now()) / 1000;
      const timeAfterAppointmentEnd = (Date.now() - scheduledEndTimestamp.getTime()) / 1000;
      return this.isBetweenDates(
        scheduledStartTimestamp,
        currentDate,
        currentDate,
        { onlyDate: true, inclusiveFrom: true, inclusiveTo: true },
      )
            && (timeFromAppointmentStart <= this.appointmentThreshold)
            && (timeAfterAppointmentEnd <= this.appointmentThreshold);
    },

    mapRecurringAppointments(recurringAppointments, startDate, endDate) {
      if (this.hasPremiumScheduling) {
        return [];
      }

      // Make sure our range is in the middle of the day to avoid daylight savings issues
      const rangeStartDate = this.adjustDate(startDate, { hour: 12, minute: 0, second: 0 });
      const rangeEndDate = this.adjustDate(endDate, { hour: 12, minute: 0, second: 0 });

      const dateArray = range(
        rangeStartDate.getTime(),
        rangeEndDate.getTime(), // Adding one day in milliseconds
        86400000, // One day in milliseconds
      );

      return chain(dateArray)
        .flatMap((time) => {
          const currentDate = new Date(time);
          const currentWeekday = this.getDay(currentDate);
          return map(
            recurringAppointments,
            (appointment) => {
              if (!includes(appointment.recurringWeekdays, currentWeekday)) {
                return null;
              }

              const scheduledStartTimestamp = appointment.scheduledStartTimestamp.toDate();
              const hour = this.getHours(scheduledStartTimestamp);
              const minute = this.getMinutes(scheduledStartTimestamp);
              const scheduledStartTs = this.adjustDate(currentDate, { hour, minute, second: 0 });

              return {
                ...appointment,
                id: `${appointment.id}_${currentWeekday}`,
                scheduledStartTimestamp: Timestamp.fromDate(scheduledStartTs),
                isRecurring: true,

              };
            },
          );
        })
        .compact()
        .value();
    },

    /**
     * Check if the appointment is already linked to any session activity
     * @param {string} appointmentId
     * @param {string} clinicId
     * @returns {Promise<boolean>}
     */
    async isAppointmentLinkedToActivities(appointmentId, clinicId) {
      // We shouldn't call the backend function in the offline mode
      if (this.offlineMode) {
        return false;
      }

      try {
        const { data } = await callFirebaseFunction('isAppointmentLinkedToActivities', { appointmentId, clinicId });
        return data.isLinked;
      } catch (err) {
        console.error(err);
        return false;
      }
    },

    async getAppointmentsWithActivityStatuses(appointments = []) {
      if (!appointments.length) {
        return appointments;
      }

      const shouldUseActivityStatusForIntegration = this.connectedClinic?.integration
        && this.integrationSettings?.allowScheduling;

      const sessionActivityIds = [
        ...new Set(appointments
          .filter((appointment) => !appointment.integration || shouldUseActivityStatusForIntegration)
          .map((appointment) => appointment.sessionActivity)
          .filter(Boolean)),
      ];

      if (!sessionActivityIds.length) {
        return appointments;
      }

      const sessionActivitiesDb = new SessionActivitiesDB();
      const chunkedIds = chunk(sessionActivityIds, 10);
      let activityChunks = [];

      try {
        // @todo: use Promise.allSettled() instead
        activityChunks = await Promise.all(
          chunkedIds.map((ids) => sessionActivitiesDb.readAll([
            ['clinic', '==', this.connectedClinic.id],
            [documentId(), 'in', ids],
          ])),
        );
      } catch (err) {
        console.error(err);
        logEvent(err, {
          context: {
            context_data: {
              connectedClinicId: this.connectedClinic.id,
              // @todo: add sessionActivities ids with rejected status after implement Promise.allSettled()
            },
          },
        });
      }

      const activities = activityChunks.flat();

      const activityMap = new Map(
        activities.map((activity) => [activity.id, activity]),
      );

      return appointments.map((appointment) => {
        const activity = activityMap.get(appointment.sessionActivity);

        return {
          ...appointment,
          ...(activity ? { activityStatus: activity.status } : {}),
        };
      });
    }, // getAppointmentsWithActivityStatuses()

    getStatusBasedOnLinkedActivity(appointment) {
      // Activity status is available for clinics without integrations, or with integrations that allow scheduling
      if (this.connectedClinic?.integration && !this.integrationSettings?.allowScheduling) {
        return appointment.status;
      }

      const sessionActivityStatuses = Object.values(ACTIVITY_STATUSES_FOR_APPOINTMENTS);

      if (sessionActivityStatuses.includes(appointment.activityStatus)) {
        return appointment.activityStatus;
      }

      return appointment.status;
    },

    getAppointmentStatusIcon(appointment) {
      const status = this.getStatusBasedOnLinkedActivity(appointment);

      return get(
        this.appointmentStatuses,
        [status, 'icon'],
        get(this.appointmentStatusColors, ['unknown', 'icon']),
      );
    },

    getAppointmentTypeLabel(appointment) {
      return get(this.$tm('appointments.type'), appointment.type ?? 'appointment');
    },

    getAppointmentStatusLabel(appointment) {
      const status = this.getStatusBasedOnLinkedActivity(appointment);
      return get(
        this.appointmentStatuses,
        [status, 'label'],
        get(this.appointmentStatusColors, ['unknown', 'label']),
      );
    },

    getAppointmentStatusColor(appointment) {
      const status = this.getStatusBasedOnLinkedActivity(appointment);
      return get(
        this.appointmentStatuses,
        [status, 'color'],
        get(this.appointmentStatusColors, ['unknown', 'color']),
      );
    },

    getFormattedAppointmentChangeValue(obj, recurrenceChangeKey, otherObj) {
      const formatFunc = get(this.appointmentChangeFormatting, [recurrenceChangeKey, 'format']);
      if (isFunction(formatFunc)) {
        return formatFunc(get(obj, recurrenceChangeKey), obj, otherObj) || this.$t('common.placeholderEmpty');
      }
      return get(obj, recurrenceChangeKey, '') || this.$t('common.placeholderEmpty');
    }, // getFormattedAppointmentChangeValue()

    isAppointmentDateChanged(originalStartTimestamp, newStartTimestamp) {
      if (!(newStartTimestamp instanceof Timestamp)
        && !(originalStartTimestamp instanceof Timestamp)) {
        return false;
      }
      if (!(newStartTimestamp instanceof Timestamp)
        || !(originalStartTimestamp instanceof Timestamp)) {
        return true;
      }

      return !this.isSameDate(newStartTimestamp.toDate(), originalStartTimestamp.toDate(), 'day');
    }, // isAppointmentDateChanged()

  },

};
